import { useCallback } from 'react';
import { useLingui } from '@lingui/react/macro';
import { usePrepareDocumentData } from './use-prepare-document-data';

export function useGenerateDocumentHTML() {
  const { t } = useLingui();
  const prepareData = usePrepareDocumentData();

  return useCallback(() => {
    const data = prepareData();
    const showDocumentHeader = data.logo || data.companyInfo;

    let html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${data.title}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }

            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              padding: 40px;
              color: #111;
              line-height: 1.6;
            }

            .document-header {
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: flex-start;
              gap: 40px;
              margin-bottom: 32px;
              width: 100%;
            }

            .document-logo {
              flex: 0 0 auto;
              max-width: 120px;
              max-height: 80px;
            }

            .document-logo img {
              width: auto;
              max-width: 120px;
              max-height: 80px;
              height: auto;
              display: block;
            }

            .company-info {
              flex: 0 0 auto;
              font-size: 13px;
              line-height: 1.6;
              color: #666;
              text-align: right;
              max-width: 400px;
              margin-left: auto;
            }

            .company-info p {
              margin: 0;
            }

            .company-info div {
              margin: 0;
            }

            h1 {
              font-size: 28px;
              font-weight: bold;
              margin-bottom: 8px;
              color: #111;
              text-align: ${data.alignment};
            }

            .subtitle {
              font-size: 15px;
              color: #666;
              margin-bottom: 32px;
              text-align: ${data.alignment};
            }

            h2 {
              font-size: 20px;
              font-weight: bold;
              margin-top: 48px;
              margin-bottom: 16px;
              color: #111;
            }

            table {
              width: 100%;
              max-width: 100%;
              border-collapse: collapse;
              border-spacing: 0;
              table-layout: fixed;
              margin-bottom: 24px;
            }

            table:first-of-type {
              margin-top: 24px;
            }

            td {
              padding: 12px 16px;
              text-align: left;
              border: none;
              border-bottom: 1px solid #e0e0e0;
              vertical-align: top;
              box-sizing: border-box;
            }

            tr:first-child td {
              border-top: 1px solid #e0e0e0;
            }

            td:first-child {
              font-weight: 500;
              color: #111;
              width: 50%;
              padding-left: 0;
            }

            td:last-child {
              color: #111;
              text-align: right;
              padding-right: 0;
            }

            .caption {
              font-size: 13px;
              color: #666;
              margin-top: 4px;
              font-weight: normal;
            }

            .primary-result {
              font-size: 18px;
              font-weight: bold;
            }

            @media print {
              body { padding: 20px; }
              .document-header {
                margin-bottom: 24px;
                gap: 30px;
              }
              .document-logo { max-width: 100px; max-height: 60px; }
              .document-logo img { max-height: 60px; }
              .company-info { font-size: 12px; max-width: 350px; }
              h1 { font-size: 24px; margin-bottom: 6px; }
              .subtitle { font-size: 14px; margin-bottom: 20px; }
              h2 { font-size: 18px; margin-top: 40px; margin-bottom: 12px; }
              table:first-of-type { margin-top: 20px; }
            }
          </style>
        </head>
        <body>
          ${
            showDocumentHeader
              ? `
          <div class="document-header">
            ${
              data.logo
                ? `
            <div class="document-logo">
              <img src="${data.logo.url}" alt="${t`Company Logo`}" />
            </div>
            `
                : ''
            }
            ${
              data.companyInfo
                ? `
            <div class="company-info">${data.companyInfo.html.replace(/<br\s*\/?>/gi, '')}</div>
            `
                : ''
            }
          </div>
          `
              : ''
          }
          <h1>${data.title}</h1>
          ${data.subtitle ? `<div class="subtitle">${data.subtitle}</div>` : ''}
    `;

    // Add input data table
    if (data.inputDataRows.length > 0) {
      html += `<table>`;

      data.inputDataRows.forEach((row) => {
        html += `
          <tr>
            <td>${row.label}</td>
            <td>${row.value}</td>
          </tr>
        `;
      });

      html += `</table>`;
    }

    // Add results table
    if (data.resultsRows.length > 0) {
      html += `<h2>${data.resultsTitle}</h2>`;
      html += `<table>`;

      data.resultsRows.forEach((row) => {
        html += `
          <tr>
            <td${row.isPrimary ? ' class="primary-result"' : ''}>${row.label}${row.caption ? `<div class="caption">${row.caption}</div>` : ''}</td>
            <td${row.isPrimary ? ' class="primary-result"' : ''}>${row.value}</td>
          </tr>
        `;
      });

      html += `</table>`;
    }

    html += `
        </body>
      </html>
    `;

    return html;
  }, [prepareData]);
}
